import { ReactNode, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../cn";


const SHELL_ICON = "/ui/shell-icon.png";

export interface InsufficientCurrencyError {
  error_type: "insufficient_currency";
  required_pearls: number | null;
  available_pearls: number | null;
  required_stars: number | null;
  available_stars: number | null;
}

interface InsufficientPearlsModalProps {
  error: InsufficientCurrencyError | null;
  onClose: () => void;
  requiredLabel: string;
  onGetPearls?: () => void;
  preview?: ReactNode;
  extraContent?: ReactNode;
  numberFractionDigits?: number;
}

export default function InsufficientPearlsModal({
  error,
  onClose,
  requiredLabel,
  onGetPearls,
  preview,
  extraContent,
  numberFractionDigits = 0,
}: InsufficientPearlsModalProps) {
  const { t } = useTranslation();
  const COOLDOWN_MS = 3000;
  const [cooldownLeft, setCooldownLeft] = useState(COOLDOWN_MS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [prevError, setPrevError] = useState(error);

  // (b) Reset cooldown when error identity changes — during render
  if (error !== prevError) {
    setPrevError(error);
    if (error) {
      setCooldownLeft(COOLDOWN_MS);
    }
  }

  // (c) Timer ticks — setState only in interval callback
  useEffect(() => {
    if (!error) return;
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, COOLDOWN_MS - elapsed);
      setCooldownLeft(left);
      if (left <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }, 50);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [error]);

  const buttonReady = cooldownLeft <= 0;
  const progressPct = ((COOLDOWN_MS - cooldownLeft) / COOLDOWN_MS) * 100;

  if (!error) return null;

  return (
    <div
      className="fixed inset-0 z-[30000] flex items-center justify-center bg-modal-backdrop-alpha-85"
      onClick={buttonReady ? onClose : undefined}
    >
      <div
        className="w-[90%] max-w-[400px] rounded-2xl bg-surface-base px-3xl py-4xl text-white shadow-[0_12px_35px_var(--color-black-alpha-45)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="m-0 mb-xl text-center [font-family:Lato,sans-serif] text-[24px] leading-[29px] font-bold tracking-[-0.747px] text-accent"
        >
          {t("Oops")}
        </h2>

        {preview}
        {extraContent}
        <p
          className="m-0 mb-3xl text-center [font-family:Lato,sans-serif] text-[16px] leading-[22px] font-normal text-white"
        >
          {t("Unfortunately, you don't have enough Pearls.")}
        </p>

        {error.required_pearls !== null && (
          <>
            <p
              className="m-0 mb-md [font-family:Lato,sans-serif] text-[14px] leading-[20px] font-normal text-white"
            >
              {requiredLabel}
            </p>
            <div
              className="mb-xl flex items-center gap-md rounded-md border border-solid border-accent-alpha-30 bg-accent-alpha-10 px-xl py-lg"
            >
              <img src={SHELL_ICON} alt="Pearls" className="h-6 w-6 object-contain" />
              <span className="[font-family:Lato,sans-serif] text-[18px] font-bold text-accent">
                {error.required_pearls.toLocaleString("en-US", { maximumFractionDigits: numberFractionDigits })}
              </span>
            </div>
          </>
        )}

        <p
          className="m-0 mb-md [font-family:Lato,sans-serif] text-[14px] leading-[20px] font-normal text-white"
        >
          {t("You have only:")}
        </p>
        <div
          className="mb-3xl flex items-center gap-md rounded-md border border-solid border-white-alpha-10 bg-white-alpha-5 px-xl py-lg"
        >
          <img src={SHELL_ICON} alt="Pearls" className="h-6 w-6 object-contain" />
          <span className="[font-family:Lato,sans-serif] text-[18px] font-bold text-white">
            {error.available_pearls !== null
              ? error.available_pearls.toLocaleString("en-US", { maximumFractionDigits: numberFractionDigits })
              : "0"}
          </span>
        </div>

        {onGetPearls && (
          <button
            onClick={buttonReady ? onGetPearls : undefined}
            disabled={!buttonReady}
            className={cn(
              'relative flex w-full items-center justify-center gap-md overflow-hidden rounded-lg border-none px-3xl py-xl [font-family:Lato,sans-serif] text-[16px] font-bold transition-all duration-300 ease-[ease]',
              buttonReady
                ? 'cursor-pointer bg-[image:var(--gradient-reward-action)] text-black shadow-[0_4px_12px_var(--color-accent-alpha-40)]'
                : 'cursor-default bg-insufficient-pearls-modal-accent text-insufficient-pearls-modal-text shadow-none',
            )}
          >
            {/* Progress fill background */}
            {!buttonReady && (
              <>
                <div
                  className="absolute top-0 bottom-0 left-0 bg-insufficient-pearls-modal-surface transition-[width] duration-[50ms] ease-linear"
                  style={{
                    width: `${progressPct}%`,
                    borderRadius: progressPct < 100 ? "16px 0 0 16px" : 16,
                  }}
                />
                <div
                  className="absolute top-0 right-0 bottom-0 bg-insufficient-pearls-modal-accent"
                  style={{
                    width: `${100 - progressPct}%`,
                    borderRadius: progressPct > 0 ? "0 16px 16px 0" : 16,
                  }}
                />
              </>
            )}
            <img src={SHELL_ICON} alt="Pearls" className="relative z-[1] h-[19px] w-6 object-contain" />
            <span className="relative z-[1]">
              {buttonReady ? t("Buy Pearls") : `${t("Buy Pearls")} (${Math.ceil(cooldownLeft / 1000)})`}
            </span>
          </button>
        )}

        <button
          onClick={onClose}
          className="mt-lg w-full cursor-pointer rounded-lg border border-solid border-white-alpha-20 bg-transparent px-3xl py-lg [font-family:Lato,sans-serif] text-[14px] font-normal text-white"
        >
          {t("Close")}
        </button>
      </div>
    </div>
  );
}
